import { RepairRequest } from '../types/firestore';
import {
  getRepairRequestIdentificationPresentation,
  getRepairRequestPresentation,
} from '../utils/repairRequestPresentation';
import { buildRepairRequestReadModel } from '../utils/repairRequestReadModel';
import {
  getRepairRequestOperationalIndexState,
  type RepairRequestOperationalIndexState,
} from './repairRequestDisplay';

export type RepairRequestPrintVariantId = 'identity' | 'receipt' | 'label' | 'seal';
export type RepairRequestPrintVariantAvailability = 'ready' | 'locked' | 'planned';

export interface RepairRequestPrintVariant {
  id: RepairRequestPrintVariantId;
  title: string;
  description: string;
  printHint: string;
  requiresOperationalIndex: boolean;
  availability: RepairRequestPrintVariantAvailability;
}

export interface RepairRequestPrintHubModel {
  requestId: string;
  requestIndex: string | null;
  operationalIndex: RepairRequestOperationalIndexState;
  serviceReferenceLabel: string;
  clientLabel: string;
  clientEmail: string | null;
  deviceLabel: string;
  brandModelLabel: string | null;
  issueSummary: string;
  issueDescription: string;
  statusLabel: string;
  stageLabel: string;
  nextStep: string;
  createdAtLabel: string;
  updatedAtLabel: string;
  operationalIndexAssignedAtLabel: string | null;
  variants: RepairRequestPrintVariant[];
}

const PRINT_VARIANT_DEFINITIONS: Omit<RepairRequestPrintVariant, 'availability'>[] = [
  {
    id: 'identity',
    title: 'Identyfikator sprawy',
    description: 'Szybki wydruk do teczki, rejestru i przyjęcia sprawy.',
    printHint: 'Wariant ograniczony działa także bez indeksu operacyjnego.',
    requiresOperationalIndex: false,
  },
  {
    id: 'receipt',
    title: 'Potwierdzenie przyjęcia',
    description: 'Pełne potwierdzenie przyjęcia z widocznym indeksem operacyjnym.',
    printHint: 'Dostępne po nadaniu indeksu operacyjnego.',
    requiresOperationalIndex: true,
  },
  {
    id: 'label',
    title: 'Etykieta / naklejka',
    description: 'Kompaktowa etykieta na sprzęt, pojemnik lub kartę serwisową.',
    printHint: 'Dostępne po nadaniu indeksu operacyjnego.',
    requiresOperationalIndex: true,
  },
  {
    id: 'seal',
    title: 'Plomba',
    description: 'Przyszły wariant plomby operacyjnej.',
    printHint: 'W przygotowaniu.',
    requiresOperationalIndex: true,
  },
];

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const toDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (
    typeof value === 'object' &&
    typeof (value as { toDate?: () => Date }).toDate === 'function'
  ) {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }

  return null;
};

const formatPrintDateTime = (value: unknown): string | null => {
  const date = toDate(value);
  if (!date) {
    return null;
  }

  return date.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getClientLabel = (request: RepairRequest): string =>
  normalizeText(request.clientDetails?.displayName) ||
  normalizeText(request.clientDetails?.email) ||
  normalizeText(request.clientId) ||
  'Brak danych klienta';

const getServiceReferenceLabel = (request: RepairRequest): string =>
  normalizeText(request.serviceProviderId) || 'Brak danych serwisu';

const getPrintVariantAvailability = (
  variantId: RepairRequestPrintVariantId,
  requiresOperationalIndex: boolean,
  hasOperationalIndex: boolean
): RepairRequestPrintVariantAvailability => {
  if (variantId === 'seal') {
    return 'planned';
  }

  if (requiresOperationalIndex && !hasOperationalIndex) {
    return 'locked';
  }

  return 'ready';
};

export const buildRepairRequestPrintHubModel = (
  request: RepairRequest
): RepairRequestPrintHubModel => {
  const readModel = buildRepairRequestReadModel(request);
  const presentation = getRepairRequestPresentation(request, 'service');
  const identification = getRepairRequestIdentificationPresentation(request, 240);
  const operationalIndex = getRepairRequestOperationalIndexState(request);
  const hasOperationalIndex = operationalIndex.isAssigned;

  const variants = PRINT_VARIANT_DEFINITIONS.map((definition) => ({
    ...definition,
    availability: getPrintVariantAvailability(
      definition.id,
      definition.requiresOperationalIndex,
      hasOperationalIndex
    ),
  }));

  return {
    requestId: readModel.requestId || normalizeText(request.id) || normalizeText(request.uid) || '',
    requestIndex: normalizeText(request.requestIndex),
    operationalIndex,
    serviceReferenceLabel: getServiceReferenceLabel(request),
    clientLabel: getClientLabel(request),
    clientEmail: normalizeText(request.clientDetails?.email),
    deviceLabel: identification.device.label,
    brandModelLabel: normalizeText(identification.device.brandModelLabel),
    issueSummary: identification.problemSummary,
    issueDescription: identification.problemDescription || identification.problemSummary,
    statusLabel: presentation.statusLabel,
    stageLabel: presentation.stageLabel,
    nextStep: presentation.nextStep,
    createdAtLabel: formatPrintDateTime(request.createdAt) || 'Brak daty',
    updatedAtLabel: formatPrintDateTime(request.updatedAt || request.createdAt) || 'Brak daty',
    operationalIndexAssignedAtLabel:
      formatPrintDateTime(request.serviceOperationalIndexAssignedAt) || null,
    variants,
  };
};

export const isOperationalPrintVariant = (variantId: RepairRequestPrintVariantId): boolean =>
  variantId === 'receipt' || variantId === 'label';
